// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2022 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//
import { CustomAppState } from "@gcg/App/Store/Reducers";
import { getProductProperties } from "@gcg/App/Store/Selectors/ProductDetailsSelectors";
import { screen } from "@gcg/XDL/Helpers";
import Info from "@gcg/XDL/Icons/Info";
import color from "@gcg/XDL/Utilities/color";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { HasProductContext, withProductContext } from "@insite/client-framework/Components/ProductContext";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import ProductPrice, { ProductPriceStyles } from "@insite/content-library/Components/ProductPrice";
import Icon, { IconPresentationProps } from "@insite/mobius/Icon";
import Popover, { ContentBodyProps, OverflowWrapperProps, PopoverPresentationProps } from "@insite/mobius/Popover";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import * as React from "react";
import { connect } from "react-redux";
import { css } from "styled-components";

type OwnProps = WidgetProps & HasProductContext & ReturnType<typeof mapStateToProps>;

const mapStateToProps = (state: CustomAppState, ownProps: HasProductContext) => ({
    productSettings: getSettingsCollection(state).productSettings,
    customSettings: getSettingsCollection(state).customSettings,
    properties: getProductProperties(state, ownProps.productContext?.product),
});

export interface VariantTitleStyles {
    wrapper?: InjectableCss;
    label?: TypographyPresentationProps;
    icon?: IconPresentationProps;
}

export interface ProductDetailsPriceStyles {
    wrapper?: InjectableCss;
    productPrice?: ProductPriceStyles;
    infoPopover?: PopoverPresentationProps;
    popoverWrapper?: OverflowWrapperProps;
    popoverContentBody?: ContentBodyProps;
    variantTitleStyles?: VariantTitleStyles;
    parentPrice?: InjectableCss;
}

const priceTextProps = {
    size: 28,
    weight: 600,
    lineHeight: "32px",
    css: css`
        ${screen("lte", "sm")`
            font-size: 16px;
            font-weight: 600;
            line-height: 32px;
        `}
    `,
};

export const priceStyles: ProductDetailsPriceStyles = {
    wrapper: {
        css: css`
            border-bottom: 1px solid ${getColor("common.ui2")};
            padding-bottom: 10px;
            margin-bottom: 15px;

            ${screen("lte", "sm")`
                border-bottom: none;
            `}
        `,
    },
    productPrice: {
        wrapper: {
            css: css`
                display: flex;
                flex-direction: column;
                align-items: center;
                width: 100%;

                ${screen("lte", "sm")`
                    align-items: flex-start;
                    justify-content: center;
                `}
            `,
        },
        packWrapper: {
            // hide pack from the price because it is displayed in another place.
            css: css`
                display: none;
            `,
        },
        price: {
            priceText: { ...priceTextProps },
            unitOfMeasureText: { ...priceTextProps },
            realTimeText: { ...priceTextProps },
        },
        quoteMessage: {
            text: { ...priceTextProps },
        },
    },
    variantTitleStyles: {
        wrapper: {
            css: css`
                display: flex;
                align-items: center;

                ${screen("lte", "sm")`
                    display: none;
                `}
            `,
        },
        label: {
            size: 20,
            lineHeight: "32px",
            weight: 600,
            css: css`
                flex: 1 1 100%;
            `,
        },
        icon: {
            size: 16,
            color: "text.main",
            css: css`
                margin-left: 15px;
            `,
        },
    },
    infoPopover: {
        transitionDuration: "short",
        toggle: false,
        xPosition: "start",
        yPosition: "top",
    },
    popoverWrapper: {
        as: "div",
        _height: "auto",
        css: css`
            position: static;
            width: 16px;
            flex: 0 0 32px;
        `,
    },
    popoverContentBody: {
        as: "div",
        _height: "100%",
        css: css`
            font-size: 12px;
            letter-spacing: 0;
            line-height: 18px;
            padding: 15px;
            box-shadow: 0 4px 8px 0 ${color("common.backgroundContrast", 0.2)};
            position: absolute !important;
            top: auto !important;
            bottom: 100% !important;
            right: 0 !important;
            left: auto !important;
            overflow: hidden;
            height: auto;
            width: 240px;
            max-width: none;
            max-height: none;
        `,
    },
    parentPrice: {
        css: css`
            display: flex;
            align-items: center;
            line-height: 32px;
            font-size: 28px;
            font-weight: 600;
            justify-content: center;
        `,
    },
};

const styles = priceStyles;

const ProductDetailsPrice: React.FC<OwnProps> = ({ productContext, productSettings, customSettings, properties }) => {
    const displayHighLowPricesForParentProducts =
        (customSettings as any).displayHighLowPricesForParentProducts === "True";

    return (
        <StyledWrapper {...styles.wrapper}>
            {productContext.product.isVariantParent && (
                <>
                    {displayHighLowPricesForParentProducts && (
                        <StyledWrapper {...styles.parentPrice}>
                            <StyledWrapper>{properties?.lowPrice}</StyledWrapper>
                            {properties?.highPrice && properties?.lowPrice !== properties?.highPrice && (
                                <StyledWrapper>
                                    &nbsp;-&nbsp;
                                    {properties?.highPrice}
                                </StyledWrapper>
                            )}
                        </StyledWrapper>
                    )}
                    <StyledWrapper {...styles.variantTitleStyles?.wrapper}>
                        <Typography {...styles.variantTitleStyles?.label}>
                            {translate("Select from the options below")}
                        </Typography>
                        <Popover
                            {...styles.infoPopover}
                            wrapperProps={styles.popoverWrapper}
                            contentBodyProps={styles.popoverContentBody as any}
                            popoverTrigger={<Icon src={Info} {...styles.variantTitleStyles?.icon} />}
                        >
                            {translate(
                                "You can also use the search box located in the lower left of the screen to filter options bypart number.",
                            )}
                        </Popover>
                    </StyledWrapper>
                </>
            )}
            {!productContext.product.isVariantParent && (
                <ProductPrice
                    product={productContext}
                    showLabel={false}
                    showSavings={true}
                    showSavingsAmount={productSettings.showSavingsAmount}
                    showSavingsPercent={productSettings.showSavingsPercent}
                    extendedStyles={styles.productPrice}
                />
            )}
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: withProductContext(connect(mapStateToProps)(ProductDetailsPrice)),
    definition: {
        displayName: "Price",
        group: "Product Details",
        allowedContexts: ["ProductDetailsPage"],
    },
};

export default widgetModule;
